import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Seo from "../components/Seo";

const GREEN = "#6db500";

const DOOR_IMG = {
  "Double Door": "https://customer-assets-v7afamib.emergentagent.net/job_waste-treatment-hub-1/artifacts/7ddmzi33_double%20door.png.png",
  "Single Door": "https://customer-assets-v7afamib.emergentagent.net/job_waste-treatment-hub-1/artifacts/33a09xot_single%20door.png.png",
};
const CHAMBER_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_waste-treatment-hub-1/artifacts/iudl8yu1_Image%2002.png";

// Approved technical data for the full Steam Sterilizer family (from the approved reference PDF).
const STEAM_NOTES = [
  "The device necessary for water (the reverse osmosis system at least 3 bar pressure 3/4 inch).",
  "The expense of the device connection (at least 2 inch pipe or galvanized pipe resistant to 150 degrees).",
  "The air necessary for the device (1/2 inch minimum 6 bar, dry air).",
];
const STEAM_REQ = ["380 VAC", "Required", "Required", "Required"];
// [liter, stu, chamber W,H,D, device W,H,D, gen liter, gen kW]
const STEAM_ROWS = [
  [160, 1, 400, 400, 1000, 870, 1650, 1350, 50, 20],
  [250, 1, 500, 500, 800, 970, 1750, 1150, 50, 30],
  [330, 1, 500, 500, 1000, 970, 1750, 1350, 50, 30],
  [420, 2, 500, 500, 1200, 970, 1750, 1550, 50, 30],
  [555, 4, 670, 670, 800, 1140, 1900, 1050, 50, 30],
  [650, 6, 670, 670, 1000, 1140, 1900, 1400, 50, 40],
  [750, 8, 670, 670, 1250, 1140, 1900, 1600, 79, 40],
  [850, 10, 670, 670, 1550, 1140, 1900, 1900, 79, 50],
];
const STEAM_DATA = {};
["SD", "SN"].forEach((prefix) => {
  STEAM_ROWS.forEach(([liter, stu, cw, ch, cd, dw, dh, dd, gl, gp]) => {
    STEAM_DATA[`${prefix.toLowerCase()}${liter}`] = {
      model: `${prefix} 0${liter}`, stu: String(stu), liter: String(liter),
      chamber: [String(cw), String(ch), String(cd)],
      device: [String(dw), String(dh), String(dd)],
      gen: [String(gl), `${gp} kW`], req: STEAM_REQ, notes: STEAM_NOTES,
    };
  });
});

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-4">
    <span className="h-6 w-1" style={{ backgroundColor: GREEN }} />
    <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900 uppercase">{children}</h2>
  </div>
);

const Cell = ({ children }) => <td className="border border-slate-200 px-4 py-4">{children}</td>;

export default function SteamProduct({ product }) {
  useEffect(() => { window.scrollTo(0, 0); }, [product.slug]);

  const door = product.doorType; // "Single Door" | "Double Door"
  const category = `${door} Steam Sterilizer`;
  const num = product.name.replace(/^[A-Z]+/, "");
  const data = STEAM_DATA[product.slug];
  const modelCode = data ? data.model : `${product.name.slice(0, 2)} 0${num}`;
  const v = (x) => (data ? x : "—");

  return (
    <>
      <Seo
        title={`${product.name} ${category} | STERILTRON`}
        description={`STERILTRON ${product.name} ${door.toLowerCase()} steam sterilizer for efficient, reliable and controlled sterilization of heat- and moisture-resistant materials using pressurized saturated steam.`}
        path={`/products/${product.slug}`}
      />

      <div className="pt-28 md:pt-36 pb-8">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link><ChevronRight size={12} />
            <Link to="/products" className="hover:text-slate-700">Products</Link><ChevronRight size={12} />
            <Link to="/products?category=steam-sterilizers" className="hover:text-slate-700">Steam Sterilizers</Link><ChevronRight size={12} />
            <span className="text-slate-600">{door}</span>
          </nav>

          <header className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: GREEN }}>{category}</p>
            <h1 className="mt-4 font-heading text-6xl md:text-8xl font-extrabold tracking-tighter text-slate-900 leading-none">{product.name}</h1>
            <div className="mt-8 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
              <p>
                {product.name} Steam Sterilizer is designed to provide efficient, reliable and controlled
                sterilization of heat- and moisture-resistant materials using pressurized saturated steam.
                Suitable for both packaged and unpackaged materials, it supports professional sterilization
                processes across hospitals, operating theatres, laboratories, universities and other
                healthcare and research environments.
              </p>
              <p>
                Its fully automatic, microprocessor-controlled operation enables precise management and
                monitoring of the sterilization process. Programmable cycles, vacuum and steam control,
                drying phases, process monitoring, data recording and integrated safety functions help
                ensure consistent performance, operational safety and reliable sterilization results.
              </p>
            </div>
          </header>
        </div>
      </div>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="py-12 md:py-16 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <SectionTitle>Technical Specifications</SectionTitle>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-sm" data-testid={`${product.slug}-spec-table`}>
              <thead>
                <tr className="text-slate-700">
                  <th rowSpan={2} className="border border-slate-200 bg-slate-50 px-4 py-3 text-left align-middle font-semibold">Model</th>
                  <th rowSpan={2} className="border border-slate-200 bg-slate-50 px-4 py-3 text-center align-middle font-semibold">STU</th>
                  <th rowSpan={2} className="border border-slate-200 bg-slate-50 px-4 py-3 text-center align-middle font-semibold">Liter</th>
                  <th colSpan={3} className="border border-slate-200 px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-xs" style={{ color: GREEN }}>Chamber Dimension (mm)</th>
                  <th colSpan={3} className="border border-slate-200 px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-xs" style={{ color: GREEN }}>Device Dimension (mm)</th>
                  <th colSpan={2} className="border border-slate-200 px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-xs" style={{ color: GREEN }}>Generator</th>
                  <th colSpan={4} className="border border-slate-200 px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-xs" style={{ color: GREEN }}>Requirements for Installation</th>
                </tr>
                <tr className="text-slate-500 text-xs">
                  {["Width", "Height", "Deep", "Width", "Height", "Deep", "Liter", "Power", "Electric"].map((h, i) => (
                    <th key={i} className="border border-slate-200 bg-slate-50 px-4 py-2 font-medium">{h}</th>
                  ))}
                  <th className="border border-slate-200 bg-slate-50 px-4 py-2 font-medium">Water <span style={{ color: GREEN }}>■</span></th>
                  <th className="border border-slate-200 bg-slate-50 px-4 py-2 font-medium">Drain <span style={{ color: GREEN }}>●</span></th>
                  <th className="border border-slate-200 bg-slate-50 px-4 py-2 font-medium">Air <span style={{ color: GREEN }}>▲</span></th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-slate-800 text-center">
                  <td className="border border-slate-200 px-4 py-4 text-left font-heading font-bold text-slate-900">{modelCode}</td>
                  <Cell>{v(data?.stu)}</Cell>
                  <Cell><span className="font-semibold">{v(data?.liter)}</span></Cell>
                  <Cell>{v(data?.chamber[0])}</Cell><Cell>{v(data?.chamber[1])}</Cell><Cell>{v(data?.chamber[2])}</Cell>
                  <Cell>{v(data?.device[0])}</Cell><Cell>{v(data?.device[1])}</Cell><Cell>{v(data?.device[2])}</Cell>
                  <Cell>{v(data?.gen[0])}</Cell><Cell>{v(data?.gen[1])}</Cell>
                  <Cell>{v(data?.req[0])}</Cell><Cell>{v(data?.req[1])}</Cell><Cell>{v(data?.req[2])}</Cell><Cell>{v(data?.req[3])}</Cell>
                </tr>
              </tbody>
            </table>
          </div>

          {!data && (
            <p className="mt-4 text-xs md:text-sm text-slate-400" data-testid={`${product.slug}-specs-pending`}>
              Final technical specifications for {product.name} will be published once the approved specification document is available.
            </p>
          )}

          {/* INSTALLATION REQUIREMENTS */}
          <div className="mt-8 space-y-2.5 text-xs md:text-sm text-slate-500 leading-relaxed max-w-3xl" data-testid={`${product.slug}-install-notes`}>
            {data ? (
              <>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>■</span> {data.notes[0]}</p>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>●</span> {data.notes[1]}</p>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>▲</span> {data.notes[2]}</p>
              </>
            ) : (
              <>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>■</span> Water supply requirement — to be confirmed with the approved specification.</p>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>●</span> Drain connection requirement — to be confirmed with the approved specification.</p>
                <p><span className="font-semibold mr-1" style={{ color: GREEN }}>▲</span> Compressed air requirement — to be confirmed with the approved specification.</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* PRODUCT IMAGE GALLERY */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid={`${product.slug}-gallery`}>
            <div className="border border-slate-200 bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
              <img src={DOOR_IMG[door]} alt={`STERILTRON ${product.name} ${door.toLowerCase()} steam sterilizer`} loading="lazy" className="w-full h-full object-contain" />
            </div>
            {door === "Double Door" ? (
              <div className="border border-slate-200 bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
                <img src={CHAMBER_IMG} alt={`STERILTRON ${product.name} open sterilization chamber`} loading="lazy" className="w-full h-full object-contain" />
              </div>
            ) : (
              <Placeholder />
            )}
            <Placeholder />
          </div>
        </div>
      </section>
    </>
  );
}

const Placeholder = () => (
  <div className="border border-dashed border-slate-300 bg-slate-50 aspect-[4/3] flex flex-col items-center justify-center text-center px-6" data-testid="steam-image-placeholder">
    <span className="w-10 h-10 border border-slate-300 flex items-center justify-center mb-3" style={{ color: GREEN }}>+</span>
    <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">STERITRON Image</span>
    <span className="text-xs text-slate-400 mt-1">Coming soon</span>
  </div>
);
